import { RingadoChatWidget } from '@core/widget.js';
import { logger } from '@utils/logger.js';

/**
 * Ringado Autonomous Chat Widget
 * Version: 2.0.0 (Vite Build)
 *
 * Widget de chat autonome compatible avec l'API Ringado
 *
 * Usage via script tag:
 * <script
 *   src="https://widget.ringado.com/widget/ringado-chat-widget.js"
 *   type="module"
 *   data-operator-id="nn9qoj8kdin25nf"
 * ></script>
 */

// Variables globales pour le widget
let widgetInstance = null;
let isInitialized = false;

// Vérifier si déjà initialisé
if (window.RingadoChat) {
  console.warn('Ringado Chat Widget: Already initialized');
} else {
  /**
   * Factory function pour créer ou récupérer l'instance du widget
   */
  function createWidget(userConfig = {}) {
    if (widgetInstance) {
      logger.warn(
        'Widget instance already exists, returning existing instance'
      );
      return widgetInstance;
    }

    widgetInstance = new RingadoChatWidget(userConfig);
    isInitialized = true;

    // Store reference globally for messaging
    // Only spread if window.RingadoChatWidget is a plain object, not the class constructor
    const existingWidget = window.RingadoChatWidget;
    window.RingadoChatWidget = {
      ...(existingWidget &&
      typeof existingWidget === 'object' &&
      !existingWidget.prototype
        ? existingWidget
        : {}),
      instance: widgetInstance,
    };

    return widgetInstance;
  }

  /**
   * Détecte la configuration depuis le script tag ou window.RingadoConfig
   */
  function detectConfig() {
    // 1. Chercher dans le script tag actuel
    const currentScript =
      document.currentScript ||
      document.querySelector('script[data-operator-id]') ||
      document.querySelector('script[operator-id]');
    if (currentScript) {
      const operatorId =
        currentScript.getAttribute('data-operator-id') ||
        currentScript.getAttribute('operator-id');
      if (operatorId) {
        return {
          operatorId,
          baseUrl:
            currentScript.getAttribute('data-base-url') ||
            currentScript.getAttribute('base-url') ||
            'https://ringado.com',
          requireEmail:
            (currentScript.getAttribute('data-require-email') ||
              currentScript.getAttribute('require-email')) === 'true',
          theme:
            currentScript.getAttribute('data-theme') ||
            currentScript.getAttribute('theme') ||
            'default',
        };
      }
    }

    // 2. Fallback vers window.RingadoConfig
    return window.RingadoConfig || null;
  }

  // Auto-initialization
  function tryAutoInit() {
    const config = detectConfig();

    if (config && config.operatorId) {
      try {
        const widget = createWidget(config);
        logger.info('Widget auto-initialized successfully', widget);
      } catch (error) {
        logger.error('Auto-initialization failed:', error);
      }
    } else {
      logger.info('No valid configuration found, skipping auto-initialization');
    }
  }

  // Initialisation basée sur l'état du DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryAutoInit);
  } else {
    // Petite delay pour permettre à la configuration d'être définie
    setTimeout(tryAutoInit, 0);
  }

  // Exposition globale
  window.RingadoChatWidget = RingadoChatWidget;

  // API globale pour la compatibilité
  window.RingadoChat = {
    init: (config) => createWidget(config),
    open: () => {
      if (widgetInstance) {
        return widgetInstance.openChat();
      } else {
        logger.error('Cannot open chat: no widget instance available');
        return false;
      }
    },
    close: () => widgetInstance?.closeChat(),
    toggle: () => widgetInstance?.toggle(),
    show: () => widgetInstance?.show(),
    hide: () => widgetInstance?.hide(),
    destroy: () => widgetInstance?.destroy(),
    sendMessage: (message) => {
      if (widgetInstance) {
        widgetInstance.elements.input.value = message;
        widgetInstance.sendMessage();
      }
    },
    getInstance: () => widgetInstance,
    isInitialized: () => isInitialized,
    getConfig: () => detectConfig(),
  };
}

// Export pour utilisation en tant que module
export { RingadoChatWidget };
export default RingadoChatWidget;
